import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { formatMoney, formatNumber, formatTRY } from "@/lib/format";
import { fmtQty } from "@/components/backorders/BackorderTable";

const r2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;

function Row({ l, v, setV }) {
  const k = l.koli_ici || 1;
  const max = Math.min(l.remaining_qty, l.available_qty);
  const q = Number(v) || 0;
  const bad = q < 0 || q > max + 1e-6;
  return (
    <tr data-testid={`pinv-row-${l.idx}`} className={bad ? "bg-rose-50" : ""}>
      <td><div className="font-mono text-[11px]">{l.sku}</div><div className="max-w-[220px] truncate" title={l.name}>{l.name}</div>{l.backorder_id && <div className="text-[10px] text-blue-700">Tamamlama · {l.backorder_source_no}</div>}</td>
      <td className="text-right font-mono">{fmtQty(l.ordered_qty, k, l.unit)}<div className="text-[10px] text-slate-400">{formatNumber(l.ordered_qty)} adet</div></td>
      <td className="text-right font-mono">{fmtQty(l.invoiced_qty, k, l.unit)}</td>
      <td className="text-right font-mono" data-testid={`pinv-available-${l.idx}`}>{fmtQty(l.available_qty, k, l.unit)}</td>
      <td className="text-right"><div className="flex items-center justify-end gap-1">
        {k > 1 && <Input type="number" min="0" className="h-8 w-[72px] text-right font-mono text-[12px]" value={r2(q / k)} onChange={(e) => setV(String(Math.max(0, Number(e.target.value) || 0) * k))} data-testid={`pinv-koli-${l.idx}`} title="Koli" />}
        <Input type="number" min="0" max={max} className="h-8 w-[90px] text-right font-mono text-[12px]" value={v} onChange={(e) => setV(e.target.value)} data-testid={`pinv-qty-${l.idx}`} title="Adet" /></div>
        {bad && <div className="text-[10px] text-rose-700">En fazla {formatNumber(max)}</div>}</td>
      <td className="text-right font-mono font-semibold" data-testid={`pinv-pending-${l.idx}`}>{fmtQty(Math.max(l.remaining_qty - q, 0), k, l.unit)}</td>
    </tr>
  );
}

export default function PartialInvoiceDialog({ order, onClose, onDone }) {
  const [pv, setPv] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [idem] = useState(() => crypto.randomUUID());
  useEffect(() => {
    api.get(`/orders/${order.id}/invoice-preview`).then(({ data }) => { setPv(data); setVals(Object.fromEntries(data.lines.map((l) => [l.idx, String(l.default_qty)]))); })
      .catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const calc = useMemo(() => {
    if (!pv) return null;
    let net = 0, vat = 0, any = false, bad = false, pend = 0;
    for (const l of pv.lines) {
      const q = Number(vals[l.idx]) || 0;
      if (q > 0) any = true;
      if (q < 0 || q > Math.min(l.remaining_qty, l.available_qty) + 1e-6) bad = true;
      const ln = r2(l.net_unit_price * q); net += ln; vat += r2(ln * l.vat_rate / 100); pend += Math.max(l.remaining_qty - q, 0);
    }
    return { net: r2(net), vat: r2(vat), gross: r2(net + vat), any, bad, pend };
  }, [pv, vals]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/invoice-partial`, { lines: pv.lines.map((l) => ({ idx: l.idx, qty: Number(vals[l.idx]) || 0 })), edit_token: pv.edit_token }, { headers: { "Idempotency-Key": idem } });
      toast.success(calc.pend > 0 ? "Kısmi fatura oluşturuldu · kalan Bekleyen Siparişlere alındı" : "Satış faturalaştırıldı");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" aria-describedby={undefined} data-testid="partial-invoice-dialog">
        <DialogHeader><DialogTitle>Faturalaştır · {order.order_no}</DialogTitle></DialogHeader>
        {!pv ? <div className="h-32 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : <>
          <p className="text-[12px] text-slate-500">Varsayılan = min(kalan, kullanılabilir stok). Kullanılabilir, bu satışın kendi rezervasyonunu içerir. Faturalanmayan miktar stoktan düşmez, cariye yazılmaz; <b>Bekleyen Siparişler</b>e alınır ve rezervasyonu serbest kalır.</p>
          <div className="overflow-x-auto max-h-[55vh]"><table className="erp-table text-[12px]">
            <thead><tr><th>Ürün</th><th className="text-right">Sipariş</th><th className="text-right">Faturalanmış</th><th className="text-right">Kullanılabilir</th><th className="text-right">Faturalaştırılacak</th><th className="text-right">Bekleyen</th></tr></thead>
            <tbody>{pv.lines.map((l) => <Row key={l.idx} l={l} v={vals[l.idx]} setV={(x) => setVals({ ...vals, [l.idx]: x })} />)}</tbody>
          </table></div>
          <div className="flex flex-wrap justify-end gap-5 text-[13px] border-t pt-2" data-testid="pinv-totals">
            <span>Net: <b className="font-mono">{formatMoney(calc.net, pv.currency)}</b></span><span>KDV: <b className="font-mono">{formatMoney(calc.vat, pv.currency)}</b></span>
            <span className="text-base">Fatura: <b className="font-mono" data-testid="pinv-total">{formatMoney(calc.gross, pv.currency)}</b>{pv.currency !== "TRY" && <span className="text-slate-500"> ({formatTRY(calc.gross * pv.fx_rate)})</span>}</span>
          </div>
          {calc.pend > 0 && <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="pinv-backorder-note">Kalan miktarlar bekleyen siparişe alınacak; stok gelince temsilciye bildirim gider.</div>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="pinv-cancel">Vazgeç</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !calc.any || calc.bad} onClick={submit} data-testid="pinv-submit">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Faturayı Oluştur</Button>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
